import React, { useState, useEffect } from 'react';
import { db, auth } from '../UI/firebaseConfig';
import { signInWithEmailAndPassword, onAuthStateChanged, signOut, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore/lite';
import { defaultExperiences } from '../UI/Experience';
import { defaultSkills } from '../UI/Skills';

const AdminPanel = () => {
    const [user, setUser] = useState(null);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [activeTab, setActiveTab] = useState('hero'); // 'hero' or 'experiences'

    const [heroData, setHeroData] = useState({
        heroTitle: 'Mohamed LEACHOURI',
        heroSubtitle1: 'Java Fullstack Developer',
        heroSubtitle2: 'Software Engineer',
        heroDescription: 'I am a Java Fullstack Web Developer with more than 4 years of experience, specializing in Java, Spring, React and Angular. Additionally, I have a strong background in DevOps practices.',
        resumeUrlEN: '',
        resumeUrlFR: '',
    });

    const [experiences, setExperiences] = useState([]);
    const [skills, setSkills] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch Hero Data
                const heroRef = doc(db, 'portfolioData', 'main');
                const heroSnap = await getDoc(heroRef);
                if (heroSnap.exists()) {
                    setHeroData(heroSnap.data());
                } else {
                    await setDoc(heroRef, heroData);
                }

                // Fetch Experiences Data
                const expRef = doc(db, 'portfolioData', 'experiences');
                const expSnap = await getDoc(expRef);
                if (expSnap.exists() && expSnap.data().items) {
                    setExperiences(expSnap.data().items);
                } else {
                    await setDoc(expRef, { items: [] });
                }

                // Fetch Skills Data
                const skillsRef = doc(db, 'portfolioData', 'skills');
                const skillsSnap = await getDoc(skillsRef);
                if (skillsSnap.exists() && skillsSnap.data().items) {
                    setSkills(skillsSnap.data().items);
                } else {
                    await setDoc(skillsRef, { items: [] });
                }
            } catch (err) {
                console.error("Error fetching data:", err);
                setError("Failed to fetch data.");
            }
        };

        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setLoading(false);
            if (currentUser) {
                fetchData();
            }
        });
        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (err) {
            setError(err.message + " (If this is your first time, you may need to register an admin first in Firebase)");
        }
    };

    // Hidden feature for user to create their first account if none exists,
    // Make sure they remove this in production later or secure it!
    const handleRegisterFirstTime = async (e) => {
        e.preventDefault();
        setError('');
        try {
            await createUserWithEmailAndPassword(auth, email, password);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleLogout = async () => {
        await signOut(auth);
    };

    const handlePushDefaults = async () => {
        if (window.confirm('This will populate (or overwrite) your Firestore data with your default hardcoded Experiences and Skills. Continue?')) {
            setSaving(true);
            try {
                const expRef = doc(db, 'portfolioData', 'experiences');
                await setDoc(expRef, { items: defaultExperiences });
                setExperiences(defaultExperiences);

                const skillsRef = doc(db, 'portfolioData', 'skills');
                await setDoc(skillsRef, { items: defaultSkills });
                setSkills(defaultSkills);

                setSuccess('Successfully populated database with default values!');
                setTimeout(() => setSuccess(''), 4000);
            } catch (err) {
                setError("Failed to push defaults: " + err.message);
            } finally {
                setSaving(false);
            }
        }
    };

    const handleHeroChange = (e) => {
        const { name, value } = e.target;
        setHeroData(prev => ({ ...prev, [name]: value }));
    };

    const handleExperienceChange = (index, field, value) => {
        const newExps = [...experiences];
        newExps[index][field] = value;
        setExperiences(newExps);
    };

    const handleExperienceArrayChange = (index, field, value) => {
        // specifically for comma separated lists like responsibilities and tech
        const newExps = [...experiences];
        newExps[index][field] = value.split(',').map(item => item.trim()).filter(item => item);
        setExperiences(newExps);
    };

    const handleSkillCategoryChange = (index, field, value) => {
        const newSkills = [...skills];
        newSkills[index][field] = value;
        setSkills(newSkills);
    };

    const handleSkillArrayChange = (index, value) => {
        const newSkills = [...skills];
        newSkills[index].skills = value.split(',').map(item => item.trim()).filter(item => item);
        setSkills(newSkills);
    };

    const addNewExperience = () => {
        setExperiences([{
            id: Date.now(),
            title: "New Position",
            company: "Company Name",
            location: "Location",
            type: "Full-time",
            period: "Jan 2024 - Present",
            duration: "1 year",
            description: "Role description...",
            responsibilities: ["Task 1", "Task 2"],
            technologies: ["React", "Java"],
            isCurrent: true
        }, ...experiences]);
    };

    const removeExperience = (index) => {
        if (window.confirm('Are you sure you want to remove this experience?')) {
            const newExps = experiences.filter((_, i) => i !== index);
            setExperiences(newExps);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            if (activeTab === 'hero') {
                const docRef = doc(db, 'portfolioData', 'main');
                await setDoc(docRef, heroData, { merge: true });
            } else if (activeTab === 'experiences') {
                const expRef = doc(db, 'portfolioData', 'experiences');
                await setDoc(expRef, { items: experiences }, { merge: true });
            } else if (activeTab === 'skills') {
                const skillsRef = doc(db, 'portfolioData', 'skills');
                await setDoc(skillsRef, { items: skills }, { merge: true });
            }
            setSuccess('Portfolio updated successfully!');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError("Failed to save changes: " + err.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center dark:text-white">Loading...</div>;

    if (!user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 p-4">
                <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-xl w-full max-w-md">
                    <h2 className="text-2xl font-bold mb-6 text-center dark:text-white">Admin Access</h2>
                    {error && <div className="bg-red-100 text-red-700 p-3 rounded mb-4 text-sm">{error}</div>}
                    <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium dark:text-gray-300 mb-1">Email</label>
                            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                                className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium dark:text-gray-300 mb-1">Password</label>
                            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
                                className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                        </div>
                        <button type="submit" className="w-full bg-primaryColor text-white p-2 rounded hover:bg-blue-600 transition">
                            Login
                        </button>
                        <button type="button" onClick={handleRegisterFirstTime} className="w-full bg-gray-600 text-white p-2 rounded hover:bg-gray-700 transition mt-2 text-sm">
                            Register (First time only)
                        </button>
                    </form>
                    <div className="mt-4 text-center">
                        <a href="/" className="text-sm text-gray-500 hover:underline">Return to Portfolio</a>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 p-4 sm:p-8">
            <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 sm:p-8">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-white mb-4 sm:mb-0">Portfolio Admin Dashboard</h1>
                    <div className="flex gap-4">
                        <button onClick={handlePushDefaults} className="bg-yellow-500 text-white px-3 sm:px-4 py-2 rounded shadow hover:bg-yellow-600 transition text-sm font-semibold">
                            Push Initial Data
                        </button>
                        <button onClick={handleLogout} className="bg-red-500 text-white px-3 sm:px-4 py-2 rounded shadow hover:bg-red-600 transition text-sm font-semibold">
                            Logout
                        </button>
                    </div>
                </div>

                {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">{error}</div>}
                {success && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">{success}</div>}

                {/* Tabs */}
                <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
                    <button
                        onClick={() => setActiveTab('hero')}
                        className={`py-3 px-6 font-medium text-sm focus:outline-none transition-colors duration-300 ${activeTab === 'hero' ? 'border-b-2 border-primaryColor text-primaryColor dark:text-primaryColorDark' : 'text-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}`}
                    >
                        Hero & CV Links
                    </button>
                    <button
                        onClick={() => setActiveTab('experiences')}
                        className={`py-3 px-6 font-medium text-sm focus:outline-none transition-colors duration-300 ${activeTab === 'experiences' ? 'border-b-2 border-primaryColor text-primaryColor dark:text-primaryColorDark' : 'text-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}`}
                    >
                        Experiences
                    </button>
                    <button
                        onClick={() => setActiveTab('skills')}
                        className={`py-3 px-6 font-medium text-sm focus:outline-none transition-colors duration-300 ${activeTab === 'skills' ? 'border-b-2 border-primaryColor text-primaryColor dark:text-primaryColorDark' : 'text-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'}`}
                    >
                        Skills
                    </button>
                </div>

                <form onSubmit={handleSave} className="space-y-6">
                    {activeTab === 'hero' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-fade-in">
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold border-b pb-2 text-gray-300 dark:border-gray-700">Hero Section Text</h3>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Main Name/Title</label>
                                    <input type="text" name="heroTitle" value={heroData.heroTitle} onChange={handleHeroChange}
                                        className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-primaryColor focus:border-transparent transition-all" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Subtitle 1</label>
                                    <input type="text" name="heroSubtitle1" value={heroData.heroSubtitle1} onChange={handleHeroChange}
                                        className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-primaryColor focus:border-transparent transition-all" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Subtitle 2</label>
                                    <input type="text" name="heroSubtitle2" value={heroData.heroSubtitle2} onChange={handleHeroChange}
                                        className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-primaryColor focus:border-transparent transition-all" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                                    <textarea name="heroDescription" value={heroData.heroDescription} onChange={handleHeroChange} rows="5"
                                        className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-primaryColor focus:border-transparent transition-all" />
                                </div>
                            </div>

                            <div className="space-y-5 bg-gray-50 dark:bg-gray-700/30 p-6 rounded-lg border border-gray-100 dark:border-gray-700">
                                <h3 className="text-lg font-bold text-gray-800 dark:text-white border-b border-gray-200 dark:border-gray-600 pb-2">CV / Resume Links</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Paste your public Google Drive links here. They will automatically update on the main page.</p>
                                <div>
                                    <label className="block text-sm font-medium dark:text-gray-300 mb-1">Resume Link (EN)</label>
                                    <input type="url" name="resumeUrlEN" value={heroData.resumeUrlEN} onChange={handleHeroChange}
                                        placeholder="https://drive.google.com/file/d/..."
                                        className="w-full p-2.5 bg-white border border-gray-300 rounded dark:bg-gray-600 dark:border-gray-500 dark:text-white focus:ring-2 focus:ring-primaryColor focus:border-transparent transition-all" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium dark:text-gray-300 mb-1">Resume Link (FR)</label>
                                    <input type="url" name="resumeUrlFR" value={heroData.resumeUrlFR} onChange={handleHeroChange}
                                        placeholder="https://drive.google.com/file/d/..."
                                        className="w-full p-2.5 bg-white border border-gray-300 rounded dark:bg-gray-600 dark:border-gray-500 dark:text-white focus:ring-2 focus:ring-primaryColor focus:border-transparent transition-all" />
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'experiences' && (
                        <div className="space-y-6 animate-fade-in">
                            <div className="flex justify-between items-center bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800 dark:text-white">Manage Experiences</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">Add, edit, or remove your professional history.</p>
                                </div>
                                <button type="button" onClick={addNewExperience} className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded shadow transition-colors font-medium text-sm focus:ring-2 focus:ring-green-400 focus:outline-none">
                                    + Add New Role
                                </button>
                            </div>

                            <div className="space-y-8">
                                {experiences.map((exp, index) => (
                                    <div key={exp.id || index} className="bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 rounded-xl p-6 shadow-sm relative overflow-hidden group">
                                        <div className="absolute top-0 right-0 p-4 flex gap-2">
                                            <button type="button" onClick={() => removeExperience(index)} className="text-red-500 hover:bg-red-50 p-2 rounded transition-colors text-sm font-semibold opacity-0 group-hover:opacity-100 focus:opacity-100">
                                                Delete
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-2">
                                            <div className="col-span-1 lg:col-span-2 space-y-4">
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Job Title</label>
                                                        <input type="text" value={exp.title || ''} onChange={(e) => handleExperienceChange(index, 'title', e.target.value)}
                                                            className="w-full p-2 border border-gray-200 rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-1 focus:ring-primaryColor" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Company</label>
                                                        <input type="text" value={exp.company || ''} onChange={(e) => handleExperienceChange(index, 'company', e.target.value)}
                                                            className="w-full p-2 border border-gray-200 rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-1 focus:ring-primaryColor" />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                    <div className="col-span-2">
                                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Location</label>
                                                        <input type="text" value={exp.location || ''} onChange={(e) => handleExperienceChange(index, 'location', e.target.value)}
                                                            className="w-full p-2 border border-gray-200 rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-1 focus:ring-primaryColor" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Type</label>
                                                        <select value={exp.type || 'Full-time'} onChange={(e) => handleExperienceChange(index, 'type', e.target.value)}
                                                            className="w-full p-2 border border-gray-200 rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-1 focus:ring-primaryColor">
                                                            <option value="Full-time">Full-time</option>
                                                            <option value="Part-time">Part-time</option>
                                                            <option value="Freelance">Freelance</option>
                                                            <option value="Full-Remote">Full-Remote</option>
                                                            <option value="Hybrid">Hybrid</option>
                                                            <option value="On-Site">On-Site</option>
                                                        </select>
                                                    </div>
                                                    <div className="flex items-end pb-2">
                                                        <label className="flex items-center gap-2 cursor-pointer">
                                                            <input type="checkbox" checked={exp.isCurrent || false} onChange={(e) => handleExperienceChange(index, 'isCurrent', e.target.checked)}
                                                                className="rounded text-primaryColor h-4 w-4" />
                                                            <span className="text-sm font-semibold dark:text-gray-300">Is Current Role</span>
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Period (e.g. Jan 2024 - Present)</label>
                                                        <input type="text" value={exp.period || ''} onChange={(e) => handleExperienceChange(index, 'period', e.target.value)}
                                                            className="w-full p-2 border border-gray-200 rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-1 focus:ring-primaryColor" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Duration (e.g. 1 year)</label>
                                                        <input type="text" value={exp.duration || ''} onChange={(e) => handleExperienceChange(index, 'duration', e.target.value)}
                                                            className="w-full p-2 border border-gray-200 rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-1 focus:ring-primaryColor" />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Short Description</label>
                                                    <textarea value={exp.description || ''} onChange={(e) => handleExperienceChange(index, 'description', e.target.value)} rows="3"
                                                        className="w-full p-2 border border-gray-200 rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-1 focus:ring-primaryColor" />
                                                </div>
                                            </div>

                                            <div className="col-span-1 space-y-4 border-l border-gray-100 dark:border-gray-700 pl-0 lg:pl-6">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                                                        Responsibilities <span className="lowercase font-normal text-gray-400">(comma-separated)</span>
                                                    </label>
                                                    <textarea
                                                        value={(exp.responsibilities || []).join(', ')}
                                                        onChange={(e) => handleExperienceArrayChange(index, 'responsibilities', e.target.value)}
                                                        rows="5"
                                                        placeholder="Developed feature X, Led team of 5, Managed deployments"
                                                        className="w-full p-2 border border-gray-200 rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-1 focus:ring-primaryColor" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                                                        Technologies <span className="lowercase font-normal text-gray-400">(comma-separated)</span>
                                                    </label>
                                                    <textarea
                                                        value={(exp.technologies || []).join(', ')}
                                                        onChange={(e) => handleExperienceArrayChange(index, 'technologies', e.target.value)}
                                                        rows="3"
                                                        placeholder="React, Java, Spring Boot, AWS"
                                                        className="w-full p-2 border border-gray-200 rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-1 focus:ring-primaryColor" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {experiences.length === 0 && (
                                    <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                                        <p className="text-gray-500 dark:text-gray-400 mb-4">You have no experiences saved yet.</p>
                                        <button type="button" onClick={addNewExperience} className="bg-primaryColor text-white px-6 py-2 rounded-full hover:bg-blue-600 transition shadow">Create your first experience entry</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'skills' && (
                        <div className="space-y-6 animate-fade-in">
                            <div className="flex justify-between items-center bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800 dark:text-white">Manage Skills</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">Edit your skills arrays for each category. Changes here immediately reflect on your portfolio.</p>
                                </div>
                            </div>
                            <div className="space-y-6">
                                {skills.map((category, index) => (
                                    <div key={category.id || index} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-5 shadow-sm">
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                            <div className="col-span-1 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 pb-4 lg:pb-0 lg:pr-6">
                                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Category Name</label>
                                                <input type="text" value={category.category || ''} onChange={(e) => handleSkillCategoryChange(index, 'category', e.target.value)}
                                                    className="w-full p-2 border border-gray-200 rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-1 focus:ring-primaryColor" />

                                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mt-4 mb-2">Icon Name (FontAwesome)</label>
                                                <input type="text" value={category.iconName || ''} onChange={(e) => handleSkillCategoryChange(index, 'iconName', e.target.value)}
                                                    className="w-full p-2 border border-gray-200 rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-1 focus:ring-primaryColor" />
                                                <p className="text-[10px] text-gray-400 mt-1">Example: FaCode, FaServer, FaMobile</p>
                                            </div>
                                            <div className="col-span-1 lg:col-span-2">
                                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                                    Skills <span className="lowercase font-normal text-gray-400">(comma-separated list)</span>
                                                </label>
                                                <textarea
                                                    value={(category.skills || []).join(', ')}
                                                    onChange={(e) => handleSkillArrayChange(index, e.target.value)}
                                                    rows="4"
                                                    className="w-full p-2 border border-gray-200 rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-1 focus:ring-primaryColor" />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="pt-8 border-t dark:border-gray-700 flex justify-end gap-4 mt-8">
                        <a href="/" className="px-6 py-2 border rounded text-gray-300 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700 transition">
                            View Portfolio
                        </a>
                        <button type="submit" disabled={saving}
                            className="px-6 py-2 bg-primaryColor text-white rounded hover:bg-blue-600 transition disabled:opacity-50">
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminPanel;
